import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { FlexAlignCenter } from 'components/primitives/flex';
import { mediaMSmMax } from 'helpers/breakpoints';
import { atom, useAtomValue } from 'jotai';
import { useBreakpoint } from 'components/BreakpointProvider';
import { useFeatureFlagQueryByNameWithAnonymousID } from 'components/layouts/hooks';
import { getFilterButtons } from './filterButtons';
import { isPriceFilterActiveAtom } from './filterStore';
import ExperienceSubTagsFilterButton from './contents/ExperienceSubTagsFilterButton';

export default function Filter(props) {
  const [dropActiveName, setDropActiveName] = useState<string>(null);
  const [filterButtons, setFilterButtons] = useState(getFilterButtons());
  const scrollContainerRef = useRef(null);
  const buttonRefs = useRef({});
  const dummyRef = useRef(null);
  const filterActive = useAtomValue(isPriceFilterActiveAtom);
  const breakpoints = useBreakpoint();
  const { featureFlag: areExperienceSubTagsEnabled } =
    useFeatureFlagQueryByNameWithAnonymousID('use_experience_sub_tags');

  useEffect(() => {
    if (areExperienceSubTagsEnabled) {
      const ogFilters = getFilterButtons();
      const newList = [
        ogFilters[0],
        {
          name: 'experience_sub_tags',
          CustomButton: ExperienceSubTagsFilterButton,
          isFilterHiddenAtom: atom(false),
          isFilterActiveAtom: atom(false),
        },
        ...ogFilters.slice(1),
      ];

      setFilterButtons(newList);
    }
  }, [areExperienceSubTagsEnabled]);

  useEffect(() => {
    if (filterActive && dropActiveName === 'price' && breakpoints.sm) {
      const activeButton = buttonRefs.current[dropActiveName]?.current;
      if (activeButton) {
        setTimeout(() => {
          dummyRef.current.scrollIntoView({
            behavior: 'smooth',
          });
        }, 1);
      }
    }
  }, [filterActive, dropActiveName]);

  const toggleFilterDropdown = (filterName: string) => {
    if (dropActiveName === filterName) {
      setDropActiveName(null);
    } else {
      setDropActiveName(filterName);
      if (buttonRefs.current[filterName]?.current && breakpoints.sm) {
        const button = buttonRefs.current[filterName].current;
        button.scrollIntoView({
          behavior: 'smooth',
        });
      }
    }
  };

  useEffect(() => {
    filterButtons.forEach(({ name }) => {
      buttonRefs.current[name] = React.createRef();
    });
  }, [filterButtons]);

  return (
    <FilterButtons gap="14px" flex="1" {...props} ref={scrollContainerRef}>
      {filterButtons.map(({ name, isFilterHiddenAtom, CustomButton }) => (
        <CustomButton
          key={name}
          name={name}
          toggleFilterDropdown={toggleFilterDropdown}
          dropdownActive={name === dropActiveName}
          isFilterHiddenAtom={isFilterHiddenAtom}
          containerRef={buttonRefs.current[name]}
        />
      ))}
      <div style={{ width: '150px' }} ref={dummyRef} />
    </FilterButtons>
  );
}

const FilterButtons = styled(FlexAlignCenter)<{ showTiles: boolean }>`
  padding: 0 24px;
  grid-row: ${({ showTiles }) => (showTiles ? 2 : 1)};
  ${mediaMSmMax} {
    overflow-x: scroll;
    scrollbar-width: none;
    gap: 8px;
  }
`;
